/**
 * psp.js — el catálogo de proveedores de pago, uno solo.
 *
 * POR QUÉ EXISTE
 * --------------
 * Había cuatro copias de `PSP_LABELS` en las pantallas de YoolPay y ninguna
 * coincidía con otra:
 *
 *   YoolPayConciliacion:63    6 · virtualpos toku flow kushki simulate yoolpay
 *   YoolPaySettlementList:58  3 · virtualpos toku flow
 *   YoolPayCierreContable:72  3 · virtualpos toku flow
 *   YoolPayChargesList:43     3 · toku flow virtualpos
 *
 * O sea: el mismo filtro ofrecía opciones distintas según la pantalla. Tres de
 * las cuatro no dejaban elegir `kushki`, `simulate` ni `yoolpay`, que el
 * servidor sí acepta y que sí tienen cobros que conciliar.
 *
 * LA FUENTE DE VERDAD ES EL SERVIDOR
 * ----------------------------------
 * Esta lista es copia literal de `PSP_IDS` en
 * `payment/src/workers/conciliacion.worker.ts:61`, que es contra lo que el
 * servidor VALIDA: un `psp` fuera de esa lista responde 400 con el detalle. Por
 * eso no se agregan ids "por si acaso" — un id que el servidor no conoce no es
 * una opción, es un 400 esperando que alguien lo elija.
 *
 * Lo ideal sería que payment sirviera el catálogo (así lo pide el diseño), pero
 * mientras no exista ese endpoint esto es una copia DECLARADA, con el archivo de
 * origen anotado para que se actualice junto con él. Una copia declarada es el
 * problema conocido; cuatro copias divergentes era el problema real.
 *
 * OJO: `klap` NO está aquí aunque exista `payment/src/adapters/klap.adapter.ts`.
 * Tener adaptador no es estar en el contrato: `PSP_IDS` no lo incluye, así que
 * mandarlo daría 400. Si se habilita, se agrega en payment primero.
 *
 * QUÉ NO ES ESTE ARCHIVO
 * ----------------------
 * No es el mapa de colores de marca de los proveedores. Ése es
 * `paymentProviders` en `src/styles/tokens/colors.js:449` y se queda donde está:
 * el `#00B1EA` de MercadoPago no es un token Yool y no pertenece al DS. Ese mapa
 * además está incompleto respecto de este catálogo (trae `mercadopago` y
 * `webpay`, que no están en el contrato, y le faltan `toku`, `kushki`,
 * `simulate`, `yoolpay`) — es deuda de dominio anotada aparte, no de diseño.
 *
 * @see payment/src/workers/conciliacion.worker.ts — `PSP_IDS`, la fuente
 * @see docs/design-system-unico.md §7 — por qué `paymentProviders` no se toca
 */

/**
 * Los `psp_id` que payment acepta, en el mismo orden que el contrato.
 *
 * `simulate` es el adaptador de pruebas y `yoolpay` la recaudación propia: van en
 * el catálogo porque cobraron y por lo tanto se concilian — dejarlos fuera sería
 * decidir que no existen.
 */
export const PSP_IDS = ['virtualpos', 'toku', 'flow', 'kushki', 'simulate', 'yoolpay'];

/** Cómo se llama cada proveedor en la UI. */
export const PSP_LABEL = {
  virtualpos: 'VirtualPOS',
  toku: 'Toku',
  flow: 'Flow',
  kushki: 'Kushki',
  simulate: 'Simulate (pruebas)',
  yoolpay: 'YoolPay',
};

/**
 * El PSP por omisión.
 *
 * VirtualPOS es el que mueve el volumen y el único con reporte de conciliación
 * hoy. Se declara aquí para que las pantallas no lo hardcodeen una por una.
 */
export const PSP_DEFAULT = 'virtualpos';

/**
 * Valor centinela para "todos los proveedores".
 *
 * Los `Select` de Radix no aceptan string vacío como valor, así que el "sin
 * filtro" necesita un centinela. Estaba definido a mano en las pantallas; va aquí
 * para que sea el mismo en todas.
 */
export const PSP_TODOS = '__todos__';

/** Label de un id, sin inventar: un id desconocido se muestra crudo, nunca vacío. */
export const pspLabel = (id) => PSP_LABEL[id] ?? id ?? '—';

/** ¿Es un `psp_id` que el servidor va a aceptar? */
export const esPspValido = (id) => PSP_IDS.includes(id);

/**
 * Opciones `{ value, label }` para un select.
 *
 * @param {boolean} conTodos  si incluye además la opción "todos los proveedores"
 */
export const pspOpciones = (conTodos = false) => {
  const base = PSP_IDS.map((id) => ({ value: id, label: PSP_LABEL[id] }));
  return conTodos ? [{ value: PSP_TODOS, label: 'Todos los proveedores' }, ...base] : base;
};
